import React, { lazy, Suspense, useEffect, useRef, useCallback } from "react";
import "sweetalert2/dist/sweetalert2.min.css";
import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

// Component Imports
import SideNav from "./components/SideNav/sidenav";
import Dashboard from "./DashboardPage/components/dashboard/Dashboard";
import ViewSubmission from "./pages/view-submissions/ViewSubmission";
import FormPage from "./PitchFormPage/FormPage";
import EditFormPage from "./PitchFormPage/EditFormPage";
import InviteUserPage from "./pages/admin/inviteUser";
import Loader from "./components/common/Loader";

// Authentication Page Imports
import LabelCodePage from "./pages/auth/LabelCodePage";
import { useJwtAuth } from "./hooks/useJwtAuth";
import { useAppDispatch, useAppSelector } from "./hooks/redux";
import {
  clearAuth,
  setAuth,
  setError,
  setLoading,
} from "./store/slices/authSlice";
import ForgetPasswordPage from "./pages/auth/ForgetPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import DSP from "./pages/dsp/DSP";
import UserDsps from "./pages/admin/UserDsps";
import PastSubmissions from "./pages/past-submissions/PastSubmissions";
import Labels from "./pages/labels/Labels";
import Settings from "./pages/settings/Settings";
import Analytics from "./pages/analytics/analytics";
import Transactions from "./pages/transactions/Transactions";
import Pricing from "./pages/pricing/Pricing";
import PrivacyPolicyPage from "./pages/privacy/PrivacyPolicyPage";
import Roster from "./pages/roster/Roster";
import AddArtist from "./pages/roster/add-artist/AddArtist";
import ArtistDetail from "./pages/roster/artist-details/ArtistDetail";

const SignUpPage = lazy(() => import("./pages/auth/SignUpPage"));
const LogInPage = lazy(() => import("./pages/auth/LogInPage"));

const useAuthValidation = () => {
  const { validateToken } = useJwtAuth();
  const dispatch = useAppDispatch();
  const { token } = useAppSelector((state) => state.auth);
  const validationRef = useRef(false);

  const performValidation = useCallback(async () => {
    if (!token || validationRef.current) return;

    validationRef.current = true;
    dispatch(setLoading(true));

    try {
      const tokenIsValid = await validateToken(token);

      if (!tokenIsValid) {
        throw new Error("Invalid token");
      }

      dispatch(setAuth({ isAuthenticated: true }));
    } catch (error) {
      console.error("Auth validation error:", error);
      dispatch(setError(error instanceof Error ? error.message : "Authentication failed"));
      dispatch(clearAuth());
    } finally {
      dispatch(setLoading(false));
      validationRef.current = false;
    }
  }, [validateToken, dispatch, token]);

  return performValidation;
};

interface PrivateRouteProps {
  children: React.ReactNode;
  fallbackPath?: string;
}
interface AdminRouteProps {
  children: React.ReactNode;
  fallbackPath?: string;
}

export const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  fallbackPath = "/auth/verify-label",
}) => {
  const dispatch = useAppDispatch();
  const { token, loading, isAuthenticated, user } = useAppSelector(
    (state) => state.auth
  );
  const performValidation = useAuthValidation();
  const allowRoles = ["admin", "basic"];

  useEffect(() => {
    if (!token) {
      dispatch(clearAuth());
      return;
    }
    performValidation();
  }, [token, performValidation]); // Only run when token changes

  if (loading) {
    return null;
  }

  if (!isAuthenticated || (user?.role && !allowRoles.includes(user?.role))) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

export const AdminRoute: React.FC<AdminRouteProps> = ({
  children,
  fallbackPath = "/dashboard",
}) => {
  const { loading, isAuthenticated, user } = useAppSelector(
    (state) => state.auth
  );

  if (loading) {
    return null; // Show a loading spinner if needed
  }

  // Restrict access to only admin users
  if (!isAuthenticated || user?.role !== "admin") {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="app">
    <SideNav />
    <div className="main-content">{children}</div>
  </div>
);

const App: React.FC = () => {


  return (
    <Router>
      <Routes>
        {/* Public Authentication Routes */}
        <Route path="/auth/verify-label" element={<LabelCodePage />} />
        <Route
          path="/auth/signup"
          element={
            <Suspense fallback={<Loader />}>
              <SignUpPage />
            </Suspense>
          }
        />
        <Route
          path="/auth/login"
          element={
            <Suspense fallback={<Loader />}>
              <LogInPage />
            </Suspense>
          }
        />

        <Route
          path="/auth/forget-password"
          element={
            <Suspense fallback={<Loader />}>
              <ForgetPasswordPage />
            </Suspense>
          }
        />
        <Route
          path="/auth/reset-password"
          element={
            <Suspense fallback={<Loader />}>
              <ResetPasswordPage />
            </Suspense>
          }
        />

        {/* Protected Routes - all wrapped in PrivateRoute and AuthenticatedLayout */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <Navigate to="/dashboard" replace />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <Dashboard />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        
        <Route
          path="/analytics/:id"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <Analytics />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />

        {/* Form route includes specific Header component for form page */}
        <Route
          path="/pitch/create"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <FormPage />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/invite"
          element={
            <AdminRoute>
              <AuthenticatedLayout>
                <InviteUserPage />
              </AuthenticatedLayout>
            </AdminRoute>
          }
        />

        <Route
          path="/admin/userdsp/:id"
          element={
            <AdminRoute>
              <AuthenticatedLayout>
                <UserDsps />
              </AuthenticatedLayout>
            </AdminRoute>
          }
        />

        <Route
          path="pitch/submission/:id"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <ViewSubmission />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        
        <Route
          path="pitch/edit/:id"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <EditFormPage />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/dsp"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <DSP />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/past-submissions"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <PastSubmissions />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
         <Route
          path="/roster"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <Roster />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/roster/add-artist"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <AddArtist />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
         <Route
          path="/roster/artist-details/:id"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <ArtistDetail />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
         <Route
          path="/labels"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <Labels />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
         <Route
          path="/transactions"
          element={
                 <AdminRoute>
              <AuthenticatedLayout>
                <Transactions />
              </AuthenticatedLayout>
            </AdminRoute>
          }
        />
         <Route
          path="/pricing"
          element={
             <Suspense fallback={<Loader />}>
                <Pricing />
              </Suspense>
  
          }
        />
         <Route
          path="/settings"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <Settings />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <PrivateRoute>
              <AuthenticatedLayout>
                <PrivacyPolicyPage />
              </AuthenticatedLayout>
            </PrivateRoute>
          }
        />

        {/* Catch-all route - redirects unknown paths to label verification */}
        {/* <Route path="*" element={<Navigate to="/verify-label" replace />} /> */}
      </Routes>
    </Router>
  );
};

export default App;